// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// Courier-wide volume report (migration 335). Walks the courier's order-number
// sequence, groups every order by its originating pharmacy, and lets you drill
// into one pharmacy's orders and open a POD.
//
// This page shows NO patient data, because the census stores none — see the PHI
// boundary note in db/migrations/335_carrier_order_census.sql. The POD button
// streams the document live from the courier and stores nothing.

import { useEffect, useState } from 'react'
import SearchableSelect from '../../components/common/SearchableSelect'
import { formatUsDateTime } from '../../api/format'
import { Building2, FileText, RefreshCw, ChevronLeft, AlertTriangle } from 'lucide-react'
import * as api from '../../api/client'
import { useTableFunnels, type FunnelColumn } from '../../components/common/TableFunnels'

const card: React.CSSProperties = {
  border: '1px solid var(--color-border)', borderRadius: 8, padding: 16,
  background: 'var(--color-surface, #fff)', marginBottom: 16,
}
const lbl: React.CSSProperties = {
  fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5,
  color: 'var(--color-text-muted)', fontWeight: 600,
}
const th: React.CSSProperties = {
  textAlign: 'left', padding: '8px 10px', fontSize: 11, textTransform: 'uppercase',
  letterSpacing: 0.4, color: 'var(--color-text-muted)', fontWeight: 600,
  borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap',
}
const td: React.CSSProperties = {
  padding: '8px 10px', fontSize: 13, borderBottom: '1px solid var(--color-border)',
}
const btn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 12px',
  fontSize: 13, fontWeight: 600, cursor: 'pointer', borderRadius: 6,
  border: '1px solid var(--color-border)', background: 'var(--color-surface, #fff)',
  color: 'var(--color-text)',
}

function fmtDate(iso: string | null) {
  if (!iso) return '—'
  const d = new Date(iso)
  return isNaN(d.getTime()) ? '—' : d.toLocaleDateString()
}
function money(n: number | null) {
  return n == null ? '—' : `$${n.toFixed(2)}`
}

// D-UI-2: a funnel on every column. Each value is what the cell shows; dates stay raw.
const PHARMACY_FUNNELS: FunnelColumn<api.CensusPharmacyRow>[] = [
  { key: 'pharmacy', label: 'Pharmacy', value: p => p.pharmacy },
  { key: 'order_count', label: 'Orders', value: p => p.order_count },
  { key: 'delivered_count', label: 'Delivered', value: p => p.delivered_count },
  { key: 'pod_count', label: 'With POD', value: p => p.pod_count },
  { key: 'piece_count', label: 'Pieces', value: p => p.piece_count },
  { key: 'charged_total', label: 'Charged', value: p => money(p.charged_total) },
  { key: 'first_order', label: 'First', value: p => p.first_order },
  { key: 'last_order', label: 'Last', value: p => p.last_order },
]
const ORDER_FUNNELS: FunnelColumn<api.CensusOrderRow>[] = [
  { key: 'order_number', label: 'Order', value: o => o.order_number },
  { key: 'order_status', label: 'Status', value: o => o.order_status },
  { key: 'created_date', label: 'Created', value: o => o.created_date },
  { key: 'delivered_date', label: 'Delivered', value: o => o.delivered_date },
  { key: 'service', label: 'Service', value: o => o.service },
  { key: 'pieces', label: 'Pieces', value: o => o.pieces },
  { key: 'amount_quote', label: 'Quoted', value: o => money(o.amount_quote) },
  { key: 'amount_received', label: 'Received', value: o => money(o.amount_received) },
  { key: 'cod_status', label: 'COD', value: o => o.cod_status || 'not supplied' },
  { key: 'has_pod', label: 'POD', value: o => (o.has_pod ? 'POD' : null) },
]

export default function CourierCensusPage() {
  const [jobId, setJobId] = useState('')
  const [jobs, setJobs] = useState<api.CarrierScanJob[]>([])
  const [report, setReport] = useState<api.CensusReport | null>(null)
  const [drill, setDrill] = useState<{ pharmacy: string; orders: api.CensusOrderRow[] } | null>(null)
  const [loading, setLoading] = useState(false)
  const [err, setErr] = useState<string | null>(null)

  // D-UI-2: the funnels filter every pharmacy / every order of the drill, not a page of them.
  const phFx = useTableFunnels(report?.pharmacies, PHARMACY_FUNNELS)
  const ordFx = useTableFunnels(drill?.orders, ORDER_FUNNELS)

  function readErr(e: unknown) {
    const body = (e as { body?: { message?: string; error?: string } }).body
    return body?.message || body?.error || (e as Error).message || 'Request failed.'
  }

  async function load(id: string) {
    if (!id.trim()) return
    setLoading(true); setErr(null)
    try { setReport(await api.getCensusReport(id.trim())); setDrill(null) }
    catch (e) { setErr(readErr(e)); setReport(null) }
    finally { setLoading(false) }
  }

  async function openPharmacy(name: string) {
    setLoading(true); setErr(null)
    try {
      const r = await api.getCensusPharmacyOrders(jobId.trim(), name)
      setDrill({ pharmacy: name, orders: r.orders })
      ordFx.setFilters({})   // another pharmacy's orders start unfiltered
    } catch (e) { setErr(readErr(e)) }
    finally { setLoading(false) }
  }

  async function openPod(orderNumber: string) {
    setErr(null)
    try {
      const doc = await api.globalOrderPod(orderNumber)
      window.open(doc.url, '_blank', 'noopener')
    } catch (e) { setErr(readErr(e)) }
  }

  // Find the census jobs and open the newest one. An empty box with no way to
  // discover a job id made every completed scan invisible.
  // ?job=<id> in the URL still wins, so a specific report stays linkable.
  useEffect(() => {
    void (async () => {
      const urlJob = new URLSearchParams(window.location.search).get('job')
      let list: api.CarrierScanJob[] = []
      try { list = (await api.listCarrierScans()).items.filter(j => j.is_census) } catch { /* fall back to manual entry */ }
      setJobs(list)
      const pick = urlJob || list[0]?.id
      if (pick) { setJobId(pick); await load(pick) }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const job = report?.job
  // A census that hasn't finished its range covers only part of the picture, and
  // every volume number below is therefore a floor, not a total. Say so rather
  // than letting a half-finished scan read as a complete count.
  const partial = job ? job.status !== 'completed' || job.cursor_at <= job.range_end : false
  const scanned = job ? Math.max(0, Math.min(job.cursor_at - job.range_start, job.range_end - job.range_start + 1)) : 0
  const total = job ? job.range_end - job.range_start + 1 : 0

  return (
    <div style={{ padding: 24, maxWidth: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
        <Building2 size={20} />
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0 }}>Courier volume by pharmacy</h1>
      </div>
      <p style={{ fontSize: 13, color: 'var(--color-text-muted)', marginTop: 0, marginBottom: 16 }}>
        Order volume across the courier's customers, grouped by the pharmacy that shipped it.
        Drill into a pharmacy to see its orders and open a proof of delivery.
      </p>

      <div style={card}>
        <div style={lbl}>Census scan</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap', alignItems: 'center' }}>
          {jobs.length > 0 ? (
            <SearchableSelect
              value={jobId}
              ariaLabel="Census scan"
              style={{ flex: '1 1 420px' }}
              options={jobs.map(j => ({
                value: j.id,
                label: `Orders ${j.range_start}–${j.range_end} · ${j.matched ?? 0} found · ${j.status}`,
                hint: j.created_at ? formatUsDateTime(String(j.created_at)) : undefined,
              }))}
              onChange={v => { setJobId(v); void load(v) }} />
          ) : (
            <input
              value={jobId} onChange={e => setJobId(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') void load(jobId) }}
              placeholder="Census job id"
              style={{ flex: '1 1 340px', padding: '8px 10px', fontSize: 13, borderRadius: 6,
                border: '1px solid var(--color-border)', background: 'var(--color-bg, #fff)',
                color: 'var(--color-text)' }} />
          )}
          <button type="button" style={btn} disabled={loading} onClick={() => void load(jobId)}>
            <RefreshCw size={14} /> {loading ? 'Loading…' : 'Refresh'}
          </button>
        </div>
        {jobs.length === 0 && !loading && (
          <div style={{ marginTop: 8, fontSize: 12, color: 'var(--color-text-muted)' }}>
            No census scans have been run yet.
          </div>
        )}
      </div>

      {err && (
        <div style={{ ...card, borderColor: 'var(--color-danger, #b91c1c)' }}>
          <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <AlertTriangle size={16} style={{ marginTop: 2, flexShrink: 0 }} />
            <div style={{ fontSize: 13 }}>{err}</div>
          </div>
        </div>
      )}

      {job && (
        <div style={card}>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div><div style={lbl}>Range</div><div style={{ fontSize: 15, fontWeight: 600 }}>{job.range_start}–{job.range_end}</div></div>
            <div><div style={lbl}>Scanned</div><div style={{ fontSize: 15, fontWeight: 600 }}>{scanned} / {total}</div></div>
            <div><div style={lbl}>Orders found</div><div style={{ fontSize: 15, fontWeight: 600 }}>{job.matched}</div></div>
            <div><div style={lbl}>Pharmacies</div><div style={{ fontSize: 15, fontWeight: 600 }}>{report?.count ?? 0}</div></div>
            <div><div style={lbl}>Status</div><div style={{ fontSize: 15, fontWeight: 600 }}>{job.status}</div></div>
            {job.errors > 0 && <div><div style={lbl}>Errors</div><div style={{ fontSize: 15, fontWeight: 600 }}>{job.errors}</div></div>}
          </div>
          {partial && (
            <div style={{ marginTop: 12, padding: 8, fontSize: 12, borderRadius: 6,
              border: '1px solid var(--color-border)', background: 'var(--color-bg, #f8fafc)' }}>
              This scan has not finished its range. Every count below is a <strong>floor</strong>, not a total —
              orders in the unscanned part of the range are not represented.
            </div>
          )}
          {report && !report.cod_reported && (
            <div style={{ marginTop: 8, padding: 8, fontSize: 12, borderRadius: 6,
              border: '1px solid var(--color-border)', background: 'var(--color-bg, #f8fafc)' }}>
              No order in this scan reported a COD status, so the COD column reads
              “not supplied”. That means <strong>the courier did not send the field</strong> — it does
              not mean nothing was collected.
            </div>
          )}
        </div>
      )}

      {report && !drill && (
        <>
        {phFx.chips}
        <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
          <table {...phFx.tableProps} style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>Pharmacy {phFx.funnel('pharmacy')}</th>
                <th style={th}>Orders {phFx.funnel('order_count')}</th>
                <th style={th}>Delivered {phFx.funnel('delivered_count')}</th>
                <th style={th}>With POD {phFx.funnel('pod_count')}</th>
                <th style={th}>Pieces {phFx.funnel('piece_count')}</th>
                <th style={th}>Charged {phFx.funnel('charged_total')}</th>
                <th style={th}>First {phFx.funnel('first_order')}</th>
                <th style={th}>Last {phFx.funnel('last_order')}</th>
              </tr>
            </thead>
            <tbody>
              {phFx.rows.map(p => (
                <tr key={p.pharmacy}>
                  <td style={td}>
                    <button type="button" onClick={() => void openPharmacy(p.pharmacy)}
                      style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                        color: 'var(--color-primary, #2563eb)', fontWeight: 600, fontSize: 13 }}>
                      {p.pharmacy}
                    </button>
                  </td>
                  <td style={{ ...td, fontWeight: 700 }}>{p.order_count}</td>
                  <td style={td}>{p.delivered_count}</td>
                  <td style={td}>{p.pod_count}</td>
                  <td style={td}>{p.piece_count}</td>
                  <td style={td}>{money(p.charged_total)}</td>
                  <td style={td}>{fmtDate(p.first_order)}</td>
                  <td style={td}>{fmtDate(p.last_order)}</td>
                </tr>
              ))}
              {phFx.rows.length === 0 && (
                <tr><td style={td} colSpan={8}>{phFx.active ? 'No pharmacies match the filters.' : 'No orders recorded for this job yet.'}</td></tr>
              )}
            </tbody>
          </table>
        </div>
        </>
      )}

      {drill && (
        <>
          <button type="button" style={{ ...btn, marginBottom: 12 }} onClick={() => setDrill(null)}>
            <ChevronLeft size={14} /> All pharmacies
          </button>
          <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 8 }}>
            {drill.pharmacy} <span style={{ fontWeight: 400, color: 'var(--color-text-muted)', fontSize: 14 }}>
              · {drill.orders.length} orders</span>
          </div>
          {ordFx.chips}
          <div style={{ ...card, padding: 0, overflowX: 'auto' }}>
            <table {...ordFx.tableProps} style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Order {ordFx.funnel('order_number')}</th>
                  <th style={th}>Status {ordFx.funnel('order_status')}</th>
                  <th style={th}>Created {ordFx.funnel('created_date')}</th>
                  <th style={th}>Delivered {ordFx.funnel('delivered_date')}</th>
                  <th style={th}>Service {ordFx.funnel('service')}</th>
                  <th style={th}>Pieces {ordFx.funnel('pieces')}</th>
                  <th style={th}>Quoted {ordFx.funnel('amount_quote')}</th>
                  <th style={th}>Received {ordFx.funnel('amount_received')}</th>
                  <th style={th}>COD {ordFx.funnel('cod_status')}</th>
                  <th style={th}>POD {ordFx.funnel('has_pod')}</th>
                </tr>
              </thead>
              <tbody>
                {ordFx.rows.map(o => (
                  <tr key={o.order_number}>
                    <td style={{ ...td, fontWeight: 600 }}>{o.order_number}</td>
                    <td style={td}>{o.order_status || '—'}</td>
                    <td style={td}>{fmtDate(o.created_date)}</td>
                    <td style={td}>{fmtDate(o.delivered_date)}</td>
                    <td style={td}>{o.service || '—'}</td>
                    <td style={td}>{o.pieces ?? '—'}</td>
                    <td style={td}>{money(o.amount_quote)}</td>
                    <td style={td}>{money(o.amount_received)}</td>
                    <td style={td}>{o.cod_status || <span style={{ color: 'var(--color-text-muted)' }}>not supplied</span>}</td>
                    <td style={td}>
                      {o.has_pod ? (
                        <button type="button" style={{ ...btn, padding: '4px 8px', fontSize: 12 }}
                          onClick={() => void openPod(o.order_number)}>
                          <FileText size={12} /> POD
                        </button>
                      ) : <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>—</span>}
                    </td>
                  </tr>
                ))}
                {ordFx.rows.length === 0 && (
                  <tr><td style={td} colSpan={10}>{ordFx.active ? 'No orders match the filters.' : 'No orders for this pharmacy.'}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  )
}
